import React, { useState, useEffect } from 'react';
import { Star, Trophy, Award, Crown, Flame, Target, TrendingUp, Gift, Calendar, LogOut, Settings, Plus, Trash2, Edit, CheckCircle, XCircle } from 'lucide-react';

// Configuración de la API
//const API_URL = 'http://localhost:3001/api';
//const API_URL = 'https://dilan.innovadevsolutions.com.co'; 
const API_URL = process.env.REACT_APP_API_URL;

function App() {
  const [view, setView] = useState('public'); // public, login, admin
  const [token, setToken] = useState(localStorage.getItem('scooby_token'));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  
  // Datos del dashboard público
  const [dashboard, setDashboard] = useState({
    totalPoints: 0,
    scoobyLevel: 1,
    currentStreak: 0,
    bestStreak: 0,
    achievements: [],
    recentPoints: []
  });

  // Datos del panel admin
  const [activities, setActivities] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [missions, setMissions] = useState([]);
  const [stats, setStats] = useState({});
  const [redemptions, setRedemptions] = useState([]);

  // Estados de formularios
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showAddReward, setShowAddReward] = useState(false);
  const [showAddMission, setShowAddMission] = useState(false);
  const [showAddPoints, setShowAddPoints] = useState(false);
  const [showRedeem, setShowRedeem] = useState(false);

  const [newActivity, setNewActivity] = useState({ name: '', category: 'Casa', points: 5 });
  const [newReward, setNewReward] = useState({ name: '', type: 'diario', cost: 20 });
  const [newMission, setNewMission] = useState({ title: '', description: '', bonus_points: 10, start_date: '', end_date: '' });
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [selectedReward, setSelectedReward] = useState(null);

  // Cargar datos públicos
  useEffect(() => {
    loadPublicDashboard();
    const interval = setInterval(loadPublicDashboard, 30000); // Actualizar cada 30s
    return () => clearInterval(interval);
  }, []);

  // Cargar datos de admin si hay token
  useEffect(() => {
    if (token) {
      setView('admin');
      loadAdminData();
    }
  }, [token]);

  const loadPublicDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/dashboard`);
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    }
  };

  const loadAdminData = async () => {
    try {
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [activitiesRes, rewardsRes, missionsRes, statsRes, redemptionsRes] = await Promise.all([
        fetch(`${API_URL}/activities`, { headers }),
        fetch(`${API_URL}/rewards`),
        fetch(`${API_URL}/missions`),
        fetch(`${API_URL}/stats`),
        fetch(`${API_URL}/redemptions`, { headers })
      ]);

      setActivities(await activitiesRes.json());
      setRewards(await rewardsRes.json());
      setMissions(await missionsRes.json());
      setStats(await statsRes.json());
      setRedemptions(await redemptionsRes.json());
    } catch (error) {
      console.error('Error cargando datos admin:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_URL}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      
      const data = await res.json();
      if (data.token) {
        setToken(data.token);
        localStorage.setItem('scooby_token', data.token);
        setView('admin');
        loadAdminData();
        playSound('success');
      } else {
        alert('Usuario o contraseña incorrectos');
      }
    } catch (error) {
      alert('Error al iniciar sesión');
    }
  };

  const handleLogout = () => {
    setToken(null);
    localStorage.removeItem('scooby_token');
    setView('public');
  };

  const addActivity = async () => {
    try {
      const res = await fetch(`${API_URL}/activities`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newActivity)
      });
      
      if (res.ok) {
        loadAdminData();
        setShowAddActivity(false);
        setNewActivity({ name: '', category: 'Casa', points: 5 });
        playSound('success');
      }
    } catch (error) {
      alert('Error al agregar actividad');
    }
  };

  const deleteActivity = async (id) => {
    if (!window.confirm('¿Eliminar esta actividad?')) return;
    
    try {
      await fetch(`${API_URL}/activities/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      loadAdminData();
    } catch (error) {
      alert('Error al eliminar actividad');
    }
  };

  const addReward = async () => {
    try {
      const res = await fetch(`${API_URL}/rewards`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newReward)
      });
      
      if (res.ok) {
        loadAdminData();
        setShowAddReward(false);
        setNewReward({ name: '', type: 'diario', cost: 20 });
        playSound('success');
      }
    } catch (error) {
      alert('Error al agregar premio');
    }
  };

  const addPoints = async () => {
    if (!selectedActivity) {
      alert('Selecciona una actividad');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/points`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          activity_id: selectedActivity.id,
          points: selectedActivity.points,
          date: new Date().toISOString().split('T')[0]
        })
      });
      
      if (res.ok) {
        loadPublicDashboard();
        loadAdminData();
        setShowAddPoints(false);
        setSelectedActivity(null);
        playSound('points');
      }
    } catch (error) {
      alert('Error al agregar puntos');
    }
  };

  const redeemReward = async () => {
    if (!selectedReward) {
      alert('Selecciona un premio');
      return;
    }

    if (dashboard.totalPoints < selectedReward.cost) {
      alert('¡Puntos insuficientes!');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/redeem`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          reward_id: selectedReward.id,
          points_spent: selectedReward.cost
        })
      });
      
      const data = await res.json();
      if (data.success) {
        alert(`¡Premio canjeado! Puntos restantes: ${data.voucher.remaining_points}`);
        loadPublicDashboard();
        loadAdminData();
        setShowRedeem(false);
        setSelectedReward(null);
        playSound('reward');
      }
    } catch (error) {
      alert('Error al canjear premio');
    }
  };

  const playSound = (type) => {
    // Aquí se reproducirían los sonidos
    console.log(`🔊 Sonido: ${type}`);
  };

  const getScoobySize = (level) => {
    if (level <= 2) return 80;
    if (level <= 5) return 120;
    if (level <= 10) return 160;
    return 200;
  };

  // ===== VISTA PÚBLICA =====
  if (view === 'public') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 50%, #d35400 100%)',
        padding: '20px',
        fontFamily: 'Arial, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
          borderRadius: '20px',
          padding: '30px',
          marginBottom: '30px',
          textAlign: 'center',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          border: '4px solid #d2691e'
        }}>
          <h1 style={{ 
            fontSize: '48px', 
            color: '#ffd700', 
            margin: '0 0 10px 0',
            textShadow: '3px 3px 6px rgba(0,0,0,0.5)',
            fontWeight: 'bold'
          }}>
            🐕 SCOOBY DOO - SISTEMA DE PUNTOS
          </h1>
          <p style={{ fontSize: '20px', color: '#ffe4b5', margin: 0 }}>¡Resuelve el misterio de los buenos hábitos!</p>
        </div>

        {/* Scooby y Puntos Principales */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          {/* Scooby Card */}
          <div style={{
            background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
            borderRadius: '20px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '4px solid #d2691e'
          }}>
            <h2 style={{ color: '#ffd700', marginBottom: '20px', fontSize: '28px' }}>Scooby Nivel {dashboard.scoobyLevel}</h2>
            <div style={{
              fontSize: `${getScoobySize(dashboard.scoobyLevel)}px`,
              lineHeight: 1,
              margin: '20px 0'
            }}>
              🐕
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              borderRadius: '10px',
              padding: '10px',
              marginTop: '15px'
            }}>
              <p style={{ color: '#ffe4b5', margin: 0, fontSize: '14px' }}>
                Próximo nivel en {100 - (dashboard.totalPoints % 100)} puntos
              </p>
            </div>
          </div>

          {/* Puntos Totales */}
          <div style={{
            background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
            borderRadius: '20px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '4px solid #1e8449'
          }}>
            <Trophy size={48} color="#ffd700" style={{ marginBottom: '10px' }} />
            <h2 style={{ color: '#fff', margin: '10px 0', fontSize: '24px' }}>Puntos Totales</h2>
            <div style={{ fontSize: '72px', fontWeight: 'bold', color: '#ffd700' }}>
              {dashboard.totalPoints}
            </div>
          </div>

          {/* Rachas */}
          <div style={{
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            borderRadius: '20px',
            padding: '30px',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            border: '4px solid #a93226'
          }}>
            <Flame size={48} color="#ffd700" style={{ marginBottom: '10px' }} />
            <h2 style={{ color: '#fff', margin: '10px 0', fontSize: '24px' }}>Rachas</h2>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '20px' }}>
              <div>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ffd700' }}>
                  {dashboard.currentStreak}
                </div>
                <div style={{ fontSize: '14px', color: '#ffe4b5' }}>Actual</div>
              </div>
              <div>
                <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#ffd700' }}>
                  {dashboard.bestStreak}
                </div>
                <div style={{ fontSize: '14px', color: '#ffe4b5' }}>Récord</div>
              </div>
            </div>
          </div>
        </div>

        {/* Logros */}
        <div style={{
          background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
          borderRadius: '20px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          border: '4px solid #d2691e'
        }}>
          <h2 style={{ color: '#ffd700', marginBottom: '20px', fontSize: '28px', textAlign: 'center' }}>
            🏆 Logros Desbloqueados
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
            gap: '15px'
          }}>
            {dashboard.achievements.map(achievement => (
              <div
                key={achievement.id}
                style={{
                  background: achievement.unlocked ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255,255,255,0.1)',
                  borderRadius: '15px',
                  padding: '20px',
                  textAlign: 'center',
                  border: achievement.unlocked ? '3px solid #ffd700' : '2px solid rgba(255,255,255,0.2)',
                  opacity: achievement.unlocked ? 1 : 0.5
                }}
              >
                <div style={{ fontSize: '36px', marginBottom: '10px' }}>
                  {achievement.unlocked ? '⭐' : '🔒'}
                </div>
                <div style={{ color: '#ffd700', fontSize: '14px', fontWeight: 'bold' }}>
                  {achievement.name}
                </div>
                <div style={{ color: '#ffe4b5', fontSize: '12px', marginTop: '5px' }}>
                  {achievement.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actividad Reciente */}
        <div style={{
          background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
          borderRadius: '20px',
          padding: '30px',
          marginBottom: '30px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          border: '4px solid #d2691e'
        }}>
          <h2 style={{ color: '#ffd700', marginBottom: '20px', fontSize: '28px', textAlign: 'center' }}>
            📋 Actividad Reciente
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {dashboard.recentPoints.slice(0, 5).map((point, idx) => (
              <div
                key={idx}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  borderRadius: '10px',
                  padding: '15px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
                  <div style={{ color: '#ffd700', fontSize: '16px', fontWeight: 'bold' }}>
                    {point.activity_name || 'Actividad'}
                  </div>
                  <div style={{ color: '#ffe4b5', fontSize: '12px' }}>
                    {point.category} • {new Date(point.date).toLocaleDateString()}
                  </div>
                </div>
                <div style={{
                  background: '#27ae60',
                  color: '#fff',
                  padding: '8px 15px',
                  borderRadius: '20px',
                  fontWeight: 'bold'
                }}>
                  +{point.points} pts
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Botón Login Padres */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={() => setView('login')}
            style={{
              background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
              color: '#fff',
              border: 'none',
              padding: '15px 40px',
              fontSize: '18px',
              borderRadius: '30px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
            }}
          >
            🔐 Acceso Padres
          </button>
        </div>
      </div>
    );
  }

  // ===== VISTA LOGIN =====
  if (view === 'login') {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f39c12 0%, #e67e22 50%, #d35400 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '400px',
          width: '100%',
          boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          border: '4px solid #d2691e'
        }}>
          <h1 style={{ color: '#ffd700', textAlign: 'center', marginBottom: '30px', fontSize: '32px' }}>
            🔐 Acceso Padres
          </h1>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              style={{
                width: '100%',
                padding: '15px',
                marginBottom: '15px',
                borderRadius: '10px',
                border: '2px solid #d2691e',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <input
              type="password"
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '15px',
                marginBottom: '20px',
                borderRadius: '10px',
                border: '2px solid #d2691e',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
            <button
              type="submit"
              style={{
                width: '100%',
                background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                color: '#fff',
                border: 'none',
                padding: '15px',
                fontSize: '18px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontWeight: 'bold',
                marginBottom: '15px'
              }}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => setView('public')}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '2px solid #fff',
                padding: '15px',
                fontSize: '16px',
                borderRadius: '10px',
                cursor: 'pointer'
              }}
            >
              Volver al Tablero
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ===== VISTA ADMIN =====
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #34495e 0%, #2c3e50 100%)',
      padding: '20px',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header Admin */}
      <div style={{
        background: 'linear-gradient(135deg, #8b4513 0%, #654321 100%)',
        borderRadius: '20px',
        padding: '20px 30px',
        marginBottom: '20px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        border: '3px solid #d2691e'
      }}>
        <h1 style={{ color: '#ffd700', margin: 0, fontSize: '28px' }}>🐕 Panel de Control Scooby</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={() => setView('public')}
            style={{
              background: '#3498db',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Ver Tablero Público
          </button>
          <button
            onClick={handleLogout}
            style={{
              background: '#e74c3c',
              color: '#fff',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '5px'
            }}
          >
            <LogOut size={16} /> Salir
          </button>
        </div>
      </div>

      {/* Resumen Rápido */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '15px',
        marginBottom: '20px'
      }}>
        <div style={{ background: '#27ae60', borderRadius: '15px', padding: '20px', color: '#fff' }}>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Puntos Totales</div>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{dashboard.totalPoints}</div>
        </div>
        <div style={{ background: '#e74c3c', borderRadius: '15px', padding: '20px', color: '#fff' }}>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Nivel Scooby</div>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{dashboard.scoobyLevel}</div>
        </div>
        <div style={{ background: '#f39c12', borderRadius: '15px', padding: '20px', color: '#fff' }}>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Racha Actual</div>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{dashboard.currentStreak} días</div>
        </div>
        <div style={{ background: '#9b59b6', borderRadius: '15px', padding: '20px', color: '#fff' }}>
          <div style={{ fontSize: '14px', opacity: 0.9 }}>Actividades</div>
          <div style={{ fontSize: '36px', fontWeight: 'bold' }}>{activities.length}</div>
        </div>
      </div>

      {/* Botones de Acción Rápida */}
      <div style={{
        background: '#fff',
        borderRadius: '15px',
        padding: '20px',
        marginBottom: '20px',
        display: 'flex',
        gap: '10px',
        flexWrap: 'wrap'
      }}>
        <button
          onClick={() => setShowAddPoints(true)}
          style={{
            background: '#27ae60',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={20} /> Agregar Puntos
        </button>
        <button
          onClick={() => setShowRedeem(true)}
          style={{
            background: '#e67e22',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Gift size={20} /> Canjear Premio
        </button>
        <button
          onClick={() => setShowAddActivity(true)}
          style={{
            background: '#3498db',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={20} /> Nueva Actividad
        </button>
        <button
          onClick={() => setShowAddReward(true)}
          style={{
            background: '#9b59b6',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '10px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Plus size={20} /> Nuevo Premio
        </button>
      </div>

      {/* Secciones en Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Actividades */}
        <div style={{ background: '#fff', borderRadius: '15px', padding: '20px' }}>
          <h2 style={{ marginTop: 0, color: '#2c3e50' }}>📝 Actividades</h2>
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {activities.map(activity => (
              <div
                key={activity.id}
                style={{
                  background: '#ecf0f1',
                  borderRadius: '10px',
                  padding: '15px',
                  marginBottom: '10px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div>
				  <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{activity.name}</div>
				  <div style={{ fontSize: '14px', color: '#7f8c8d' }}>
					{activity.category} • {activity.points} puntos
					</div>
				</div>
                <div style={{ display: 'flex', gap: '5px' }}>
				  <button
					onClick={() => deleteActivity(activity.id)}
					style={{
					  background: '#e74c3c',
					  color: '#fff',
					  border: 'none',
					  padding: '8px 12px',
					  borderRadius: '8px',
					  cursor: 'pointer',
					  fontSize: '12px'
					}}
				  >
					Eliminar
				  </button>
				</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Historial de Canjes */}
      <div style={{ background: '#fff', borderRadius: '15px', padding: '20px', marginTop: '20px' }}>
        <h2 style={{ marginTop: 0, color: '#2c3e50' }}>📜 Historial de Canjes</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#ecf0f1' }}>
                <th style={{ padding: '10px', textAlign: 'left' }}>Premio</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Puntos</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Canjeado por</th>
                <th style={{ padding: '10px', textAlign: 'left' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.map((redemption, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #ecf0f1' }}>
                  <td style={{ padding: '10px' }}>{redemption.reward_name}</td>
                  <td style={{ padding: '10px', color: '#e74c3c', fontWeight: 'bold' }}>
                    -{redemption.points_spent}
                  </td>
                  <td style={{ padding: '10px' }}>{redemption.redeemed_by}</td>
                  <td style={{ padding: '10px' }}>
                    {new Date(redemption.date).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* MODALES */}

      {/* Modal: Agregar Puntos */}
      {showAddPoints && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>➕ Agregar Puntos</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Selecciona la actividad completada:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {activities.map(activity => (
                <button
                  key={activity.id}
                  onClick={() => setSelectedActivity(activity)}
                  style={{
                    background: selectedActivity?.id === activity.id ? '#27ae60' : '#ecf0f1',
                    color: selectedActivity?.id === activity.id ? '#fff' : '#2c3e50',
                    border: 'none',
                    padding: '15px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: selectedActivity?.id === activity.id ? 'bold' : 'normal'
                  }}
                >
                  <div style={{ fontSize: '16px', marginBottom: '5px' }}>{activity.name}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    {activity.category} • +{activity.points} puntos
                  </div>
                </button>
              ))}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={addPoints}
                disabled={!selectedActivity}
                style={{
                  flex: 1,
                  background: selectedActivity ? '#27ae60' : '#95a5a6',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: selectedActivity ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ✓ Agregar
              </button>
              <button
                onClick={() => {
                  setShowAddPoints(false);
                  setSelectedActivity(null);
                }}
                style={{
                  flex: 1,
                  background: '#e74c3c',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Canjear Premio */}
      {showRedeem && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>🎁 Canjear Premio</h2>
            <div style={{
              background: '#f8f9fa',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '20px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#7f8c8d' }}>Puntos disponibles</div>
              <div style={{ fontSize: '36px', fontWeight: 'bold', color: '#27ae60' }}>
                {dashboard.totalPoints}
              </div>
            </div>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              Selecciona el premio a canjear:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {rewards.filter(r => r.cost <= dashboard.totalPoints).map(reward => (
                <button
                  key={reward.id}
                  onClick={() => setSelectedReward(reward)}
                  style={{
                    background: selectedReward?.id === reward.id ? '#e67e22' : '#ecf0f1',
                    color: selectedReward?.id === reward.id ? '#fff' : '#2c3e50',
                    border: 'none',
                    padding: '15px',
                    borderRadius: '10px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '14px',
                    fontWeight: selectedReward?.id === reward.id ? 'bold' : 'normal'
                  }}
                >
                  <div style={{ fontSize: '16px', marginBottom: '5px' }}>{reward.name}</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    {reward.type} • {reward.cost} puntos
                  </div>
                </button>
              ))}
              {rewards.filter(r => r.cost > dashboard.totalPoints).length > 0 && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '10px' }}>
                    Premios bloqueados (puntos insuficientes):
                  </div>
                  {rewards.filter(r => r.cost > dashboard.totalPoints).map(reward => (
                    <div
                      key={reward.id}
                      style={{
                        background: '#f8f9fa',
                        padding: '15px',
                        borderRadius: '10px',
                        marginBottom: '10px',
                        opacity: 0.5
                      }}
                    >
                      <div style={{ fontSize: '16px', marginBottom: '5px' }}>🔒 {reward.name}</div>
                      <div style={{ fontSize: '12px', color: '#e74c3c' }}>
                        Necesitas {reward.cost - dashboard.totalPoints} puntos más
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={redeemReward}
                disabled={!selectedReward}
                style={{
                  flex: 1,
                  background: selectedReward ? '#e67e22' : '#95a5a6',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: selectedReward ? 'pointer' : 'not-allowed',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ✓ Canjear
              </button>
              <button
                onClick={() => {
                  setShowRedeem(false);
                  setSelectedReward(null);
                }}
                style={{
                  flex: 1,
                  background: '#e74c3c',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nueva Actividad */}
      {showAddActivity && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>➕ Nueva Actividad</h2>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#7f8c8d', fontSize: '14px' }}>
                Nombre de la actividad:
              </label>
              <input
                type="text"
                value={newActivity.name}
                onChange={(e) => setNewActivity({...newActivity, name: e.target.value})}
                placeholder="Ej: Tender la cama"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #ecf0f1',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#7f8c8d', fontSize: '14px' }}>
                Categoría:
              </label>
              <select
                value={newActivity.category}
                onChange={(e) => setNewActivity({...newActivity, category: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #ecf0f1',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="Casa">Casa</option>
                <option value="Colegio">Colegio</option>
                <option value="Social">Social</option>
                <option value="Conducta General">Conducta General</option>
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#7f8c8d', fontSize: '14px' }}>
                Puntos:
              </label>
              <input
                type="number"
                value={newActivity.points}
                onChange={(e) => setNewActivity({...newActivity, points: parseInt(e.target.value)})}
                min="1"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #ecf0f1',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={addActivity}
                style={{
                  flex: 1,
                  background: '#27ae60',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ✓ Crear
              </button>
              <button
                onClick={() => {
                  setShowAddActivity(false);
                  setNewActivity({ name: '', category: 'Casa', points: 5 });
                }}
                style={{
                  flex: 1,
                  background: '#e74c3c',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Nuevo Premio */}
      {showAddReward && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '20px',
            padding: '30px',
            maxWidth: '500px',
            width: '90%'
          }}>
            <h2 style={{ marginTop: 0, color: '#2c3e50' }}>🎁 Nuevo Premio</h2>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#7f8c8d', fontSize: '14px' }}>
                Nombre del premio:
              </label>
              <input
                type="text"
                value={newReward.name}
                onChange={(e) => setNewReward({...newReward, name: e.target.value})}
                placeholder="Ej: 30 min TV extra"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #ecf0f1',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#7f8c8d', fontSize: '14px' }}>
                Tipo:
              </label>
              <select
                value={newReward.type}
                onChange={(e) => setNewReward({...newReward, type: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #ecf0f1',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              >
                <option value="diario">Diario (20-50 pts)</option>
                <option value="semanal">Semanal (50-150 pts)</option>
                <option value="mensual">Mensual (150-500 pts)</option>
              </select>
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', color: '#7f8c8d', fontSize: '14px' }}>
                Costo en puntos:
              </label>
              <input
                type="number"
                value={newReward.cost}
                onChange={(e) => setNewReward({...newReward, cost: parseInt(e.target.value)})}
                min="1"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '8px',
                  border: '2px solid #ecf0f1',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={addReward}
                style={{
                  flex: 1,
                  background: '#9b59b6',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}
              >
                ✓ Crear
              </button>
              <button
                onClick={() => {
                  setShowAddReward(false);
                  setNewReward({ name: '', type: 'diario', cost: 20 });
                }}
                style={{
                  flex: 1,
                  background: '#e74c3c',
                  color: '#fff',
                  border: 'none',
                  padding: '15px',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                ✗ Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
